/**
 * **The Rekordy tab's entry point** — KANON view 06: three big-three cards with a dated run, the
 * recent-PR feed, and the per-exercise table.
 *
 * ── The big three are named by the SERVER, and that matters ────────────────────────────────────
 * `big3` is keyed on `Exercise.Code` in `Data/CanonicalLifts.cs` — `barbell-bench-press`,
 * `high-bar-back-squat`, `conventional-deadlift`. The workspace's golden rule is that business
 * logic never matches by NAME, and this projection honours it by not matching at all: the three
 * entries arrive already identified, always three, in Bench → Squat → Deadlift order, null-filled
 * for a lift the client has never trained.
 *
 * The lift's own display name still comes from this app's locale rather than from the DTO's
 * `exerciseName`, exactly as mobile does it: the card's headline is "Wyciskanie", not "Wyciskanie
 * sztangi leżąc", and the exercise name is the row the per-exercise table prints.
 *
 * ── The per-exercise table is assembled, not fetched ───────────────────────────────────────────
 * The canon draws six exercises with `NAJLEPSZY · MAX 1 REP · KIEDY`, above a caption that says the
 * per-exercise endpoint exists and mobile does not draw it. That endpoint answers for **one**
 * exercise per call, so a six-row table cannot come from it without a six-request fan-out.
 *
 * The table is therefore folded out of what the tab already has — every exercise appearing in the
 * PR feed or in the big three, with its best weight, its best e1RM and the date — and the picker
 * above it adds ONE more row from the per-exercise endpoint when the trainer names an exercise.
 * That is the honest reading of the canon: the same numbers, from the reads that carry them.
 *
 * ── Every date here is ABSOLUTE, and read off `achievedAt` ─────────────────────────────────────
 * The canon prints `9 sierpnia` on a big-three card and `3 sie` in the feed and the table, and both
 * reads carry the instant to do it with: `BigLiftRecord.AchievedAt` and `PersonalRecord.AchievedAt`
 * are the UTC instants their best sets finished at. An earlier round believed the big-three record
 * shipped `AchievedAtLabel` alone and printed the relative phrase ("12 dni temu") on every surface,
 * which is what forced both date columns 22px and 12px wider than the canon draws them. It does
 * not, the label is not read at all any more, and the columns are back at 42px and 52px.
 *
 * **The formatter is pinned to UTC, deliberately.** `achievedAt` is an instant, and rendering it in
 * the trainer's browser zone moves a late-evening set onto the following day for anyone east of
 * Greenwich — the trainer would then read a date the backend's own day arithmetic disagrees with,
 * since `BuildRelativeDayLabel` floors on UTC dates. The client's own zone would be the truest
 * answer and is NOT reachable: `Customer.TimeZoneId` exists on the backend and no read this app
 * makes publishes it. Recorded as a gap rather than guessed at.
 */

export interface BigLiftSource {
  /** The contract's own key: `BenchPress | Squat | Deadlift`. */
  readonly lift: string;
  readonly exerciseName: string | null;
  readonly bestWeightKg: number | null;
  /** The UTC instant the best set finished at, or `null` where the lift was never trained. */
  readonly achievedAt: string | null;
  readonly targetWeightKg: number | null;
  readonly history: readonly { achievedAt: string; weightKg: number }[];
}

export interface PersonalRecordSource {
  readonly exerciseId: number | null;
  readonly exerciseName: string;
  /** `Weight | E1rm | Volume`. */
  readonly prKind: string;
  readonly valueKg: number | null;
  readonly e1rmKg: number | null;
  readonly reps: number | null;
  readonly achievedAt: string;
  readonly deltaVsPrevKg: number | null;
}

export interface RecordsSources {
  readonly big3: readonly BigLiftSource[];
  readonly recent: readonly PersonalRecordSource[];
}

export type PrKind = 'weight' | 'e1rm' | 'volume';

export interface BigLiftCard {
  readonly key: string;
  readonly title: string;
  readonly value: string | null;
  readonly unit: string;
  readonly whenLabel: string | null;
  /** `max 1 rep 108 kg` or the goal's target — the canon prints one aside, never both. */
  readonly aside: string | null;
  /** Real dates. Two points minimum, or the card draws no line at all. */
  readonly points: readonly { x: Date; y: number }[];
}

/** One of the three things a single set can set a record on. */
export interface PrFeedAxis {
  readonly kind: PrKind;
  readonly kindLabel: string;
  readonly value: string;
  readonly improvement: string | null;
}

/**
 * One SET that set at least one record, with every axis it set one on.
 *
 * The wire emits one entry per (set, kind) pair — `BuildRecentPrEntries` says so in as many words,
 * and adds that "a new heaviest weight is usually also a new e1RM". So the common case is two or
 * three entries describing one moment, and the feed printed them as two or three rows: the same
 * exercise, the same date, stacked, reading as three separate achievements. At `recentN: 10` that is
 * a feed of three or four real events.
 */
export interface PrFeedRow {
  readonly key: string;
  readonly exerciseName: string;
  /** Never empty. Ordered by `PR_KIND_ORDER`, so a row does not reshuffle between reads. */
  readonly axes: readonly PrFeedAxis[];
  readonly whenLabel: string;
}

export interface ExerciseRecordRow {
  readonly key: string;
  readonly exerciseName: string;
  readonly best: string | null;
  readonly bestE1rm: string | null;
  readonly whenLabel: string;
}

export interface RecordsView {
  readonly big3: readonly BigLiftCard[];
  readonly feed: readonly PrFeedRow[];
  readonly perExercise: readonly ExerciseRecordRow[];
}

export interface RecordsDeps {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly locale: string;
}

/** The contract's three keys, narrowed to the locale's three. Anything else keeps its own word. */
const LIFT_KEYS: Readonly<Record<string, string>> = {
  BenchPress: 'benchPress',
  Squat: 'squat',
  Deadlift: 'deadlift',
};

const PR_KINDS: Readonly<Record<string, PrKind>> = {
  Weight: 'weight',
  E1rm: 'e1rm',
  Volume: 'volume',
};

/**
 * The order the axes of one set are printed in.
 *
 * The bar first, then the estimate derived from it, then the volume it moved — heaviest fact to
 * softest. Fixed rather than the wire's, so a row reads the same shape every time; the backend emits
 * them in the order it happens to test the flags in.
 */
const PR_KIND_ORDER: Readonly<Record<PrKind, number>> = { weight: 0, e1rm: 1, volume: 2 };

/** The minimum a line needs to BE a line. One point is a dot claiming to be a trend. */
const MIN_PLOT_POINTS = 2;

/** Below this, a delta rounds to `0` at the one decimal these figures are printed to. */
const SMALLEST_PRINTABLE = 0.05;

/**
 * The gain over the previous best of the SAME kind, in the unit of the thing that gained.
 *
 * ── Two different quantities wear the same field name ────────────────────────────────────────
 * `DeltaVsPrevKg` is kilograms for a weight or an e1RM PR. For a VOLUME PR the backend computes it
 * as `weight × reps` (`CustomerAnalyticsProcessor`, `Delta(volume, prevVolume)`), so it is a volume
 * figure and the `Kg` in the name is misleading. It is converted and suffixed exactly as the row's
 * own value is — tonnes for volume, kilograms for the other two — because a bare `+2,5` beside
 * `85 kg` and a bare `+25` beside `12,4 t` looked like the same number.
 *
 * ── And a gain nobody can see is not reported ────────────────────────────────────────────────
 * A volume gain of 20 kg·reps is 0,02 t, which prints as `+0 t` — measured on the live feed, three
 * rows of it. A figure at the resolution of its own unit carries nothing, and `+0` reads as "no
 * improvement", which is the opposite of what a PR row means. Withheld below the smallest value the
 * printed precision can show, exactly as an absent delta already was.
 */
function improvementOf(
  deltaKg: number | null,
  kind: PrKind,
  number: Intl.NumberFormat,
  kg: string,
  t: RecordsDeps['t'],
): string | null {
  if (deltaKg == null) return null;
  const inDisplayUnits = kind === 'volume' ? deltaKg / 1000 : deltaKg;
  if (Math.abs(inDisplayUnits) < SMALLEST_PRINTABLE) return null;
  const unit = kind === 'volume' ? t('clients.detail.progress.tonnage.unit') : kg;
  const sign = inDisplayUnits > 0 ? '+' : '−';
  return `${sign}${number.format(Math.abs(inDisplayUnits))} ${unit}`;
}

/** The wire's PR kind, narrowed to one of ours. `e1rm` is the fallback the feed has always used. */
function prKindOf(wireValue: string): PrKind {
  return (Object.hasOwn(PR_KINDS, wireValue) ? PR_KINDS[wireValue] : undefined) ?? 'e1rm';
}

/**
 * `9 sierpnia` for a card, `3 sie` for a row — the canon's two widths, from one instant.
 *
 * Both are pinned to UTC. See the header: a browser-local reading moves a late set onto the next
 * day, and the client's own zone is not on the wire.
 */
function dateFormatters(locale: string): {
  readonly long: Intl.DateTimeFormat;
  readonly short: Intl.DateTimeFormat;
} {
  return {
    long: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }),
    short: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }),
  };
}

/** An instant the wire may not have sent, or may have sent unparseable. Absent stays absent. */
function formatInstant(iso: string | null, format: Intl.DateTimeFormat): string | null {
  if (iso == null) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : format.format(at);
}

export function buildRecords(
  sources: RecordsSources | null,
  deps: RecordsDeps,
): RecordsView {
  const { t, locale } = deps;
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const date = dateFormatters(locale);
  const kg = t('clients.detail.body.unitKg');
  const big3Source = sources?.big3 ?? [];
  const recent = sources?.recent ?? [];

  const big3: readonly BigLiftCard[] = big3Source.map((lift) => {
    // `Object.hasOwn` before every wire-keyed index in this file — see `entities/client`'s note on
    // why a bare lookup can resolve through `Object.prototype`.
    const localeKey = Object.hasOwn(LIFT_KEYS, lift.lift) ? LIFT_KEYS[lift.lift] : undefined;
    const points = [...lift.history]
      .sort((left, right) => left.achievedAt.localeCompare(right.achievedAt))
      .map((point) => ({ x: new Date(point.achievedAt), y: point.weightKg }));
    // The best e1RM this lift ever reached is not on the big-three DTO, so the aside falls back to
    // the goal's target where there is one. Absent both, the card prints no aside rather than a
    // dash — the headline already says everything the contract knows.
    const aside =
      lift.targetWeightKg == null
        ? null
        : t('clients.detail.records.target', { target: number.format(lift.targetWeightKg) });
    return {
      key: lift.lift,
      title:
        localeKey === undefined
          ? (lift.exerciseName ?? lift.lift)
          : t(`clients.detail.records.lifts.${localeKey}`),
      value: lift.bestWeightKg == null ? null : number.format(lift.bestWeightKg),
      unit: kg,
      whenLabel: formatInstant(lift.achievedAt, date.long),
      aside,
      points: points.length >= MIN_PLOT_POINTS ? points : [],
    };
  });

  /**
   * One row per SET, with an axis for each record that set held.
   *
   * `achievedAt` is the set's own finish instant, not a day, so the key identifies the set exactly:
   * two different sets of one exercise on one afternoon stay two rows, which is right — they are two
   * achievements. What collapses is the three entries describing ONE of them.
   *
   * Insertion order is the wire's, which is newest-first. `Map` preserves it, so the feed keeps the
   * order the backend sorted it into and nothing here re-sorts it.
   */
  const byMoment = new Map<string, { row: PrFeedRow; axes: PrFeedAxis[] }>();
  for (const entry of recent) {
    const kind = prKindOf(entry.prKind);
    // Which number is the headline depends on which kind of record it is: an e1RM PR is about the
    // estimate, a weight PR is about the bar. Mobile makes the same choice, so the two agree.
    const headline = kind === 'e1rm' ? (entry.e1rmKg ?? entry.valueKg) : (entry.valueKg ?? entry.e1rmKg);
    const axis: PrFeedAxis = {
      kind,
      kindLabel: t(`clients.detail.records.kinds.${kind}`),
      value:
        headline == null
          ? '—'
          : kind === 'volume'
            ? `${number.format(headline / 1000)} ${t('clients.detail.progress.tonnage.unit')}`
            : `${number.format(headline)} ${kg}`,
      /**
       * The gain over the previous best of the SAME kind, with the unit of the thing that gained.
       *
       * It read as a bare `+2,5` beside a value of `85 kg` and a bare `+25` beside `12,4 t`, so two
       * numbers in different quantities looked like the same one. The field is named
       * `DeltaVsPrevKg` and is kilograms for a weight or an e1RM PR — but for a VOLUME PR the
       * backend computes it as `weight × reps` (`CustomerAnalyticsProcessor`, `Delta(volume,
       * prevVolume)`), so it is a volume figure and the name is misleading. It is therefore
       * converted and suffixed exactly as the axis's own value is: tonnes for volume, kilograms for
       * the other two.
       */
      improvement: improvementOf(entry.deltaVsPrevKg, kind, number, kg, t),
    };

    const key = `${entry.exerciseName}-${entry.achievedAt}`;
    const existing = byMoment.get(key);
    if (existing === undefined) {
      byMoment.set(key, {
        row: {
          key,
          exerciseName: entry.exerciseName,
          axes: [],
          whenLabel: formatInstant(entry.achievedAt, date.short) ?? '',
        },
        axes: [axis],
      });
      continue;
    }
    // A duplicate KIND on one set cannot happen — the backend emits each at most once per set — but
    // a wire value this build does not recognise falls back to `e1rm`, and two of those would
    // otherwise print the same axis twice.
    if (!existing.axes.some((each) => each.kind === axis.kind)) existing.axes.push(axis);
  }

  const feed: readonly PrFeedRow[] = [...byMoment.values()].map(({ row, axes }) => ({
    ...row,
    axes: [...axes].sort((left, right) => PR_KIND_ORDER[left.kind] - PR_KIND_ORDER[right.kind]),
  }));

  /**
   * One row per exercise, newest first, folded out of both reads.
   *
   * Keyed on the exercise NAME because that is the only identity both reads share — the big-three
   * DTO carries no `exerciseId` at all. This is a display grouping and not a business rule, which
   * is the one place the workspace's never-match-by-name rule does not bite; a name collision
   * merges two rows in a table and decides nothing.
   */
  const byExercise = new Map<string, ExerciseRecordRow>();
  for (const lift of big3Source) {
    const name = lift.exerciseName;
    if (name == null || lift.bestWeightKg == null) continue;
    byExercise.set(name, {
      key: name,
      exerciseName: name,
      best: `${number.format(lift.bestWeightKg)} ${kg}`,
      bestE1rm: null,
      whenLabel: formatInstant(lift.achievedAt, date.short) ?? '',
    });
  }
  for (const entry of recent) {
    const existing = byExercise.get(entry.exerciseName);
    const kind = prKindOf(entry.prKind);
    const best =
      kind === 'weight' && entry.valueKg != null ? `${number.format(entry.valueKg)} ${kg}` : null;
    const e1rm = entry.e1rmKg == null ? null : `${number.format(entry.e1rmKg)} ${kg}`;
    byExercise.set(entry.exerciseName, {
      key: entry.exerciseName,
      exerciseName: entry.exerciseName,
      best: existing?.best ?? best,
      bestE1rm: existing?.bestE1rm ?? e1rm,
      whenLabel: existing?.whenLabel ?? formatInstant(entry.achievedAt, date.short) ?? '',
    });
  }

  return {
    big3,
    feed,
    perExercise: [...byExercise.values()],
  };
}
